import React from "react";
import { RouteComponentProps } from "react-router-dom";
import "../../App.css";
import { apiRequest } from "../../functions/requestMulti";
import { MainContext } from "../../contexts/MainContext";
import Title from "../../components/Title";
import InfoContainer from "../../components/InfoContainer";
import ErrorMessage from "../../components/ErrorMessage";
import SearchInput from "../../components/SearchInput";
import Table from "../../components/Table/Table";
import { RowData } from "../../Types";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import WaitingListRemoveBtn from "../../components/WaitingListRemoveBtn";
import {
  dateTosimpleDateStr,
  firebaseDateToDateObj,
} from "../../functions/utils";

type Props = {};
type ComposedProps = Props &
  RouteComponentProps<{
    clubId: string;
    branchId: string;
  }>;

export default class WaitingList extends React.Component<
  ComposedProps,
  {
    clubId: string;
    branchId: string;
    entriesLoaded: boolean;
    entryCount: number;
    entryTableArr: Array<RowData>;
    searchVal: string;
    showConfirmationModal: boolean;
    selectedEntryId: string;
    errorMsg: string | null;
  }
> {
  static contextType = MainContext;
  mounted = true;

  constructor(props: ComposedProps) {
    super(props);
    this.state = {
      clubId: this.props.match.params.clubId,
      branchId: this.props.match.params.branchId,
      entriesLoaded: false,
      entryCount: 0,
      entryTableArr: [],
      searchVal: "",
      showConfirmationModal: false,
      selectedEntryId: "",
      errorMsg: null,
    };
  }

  componentDidMount = async () => {
    await this.requestEntries();
  };

  componentWillUnmount = () => {
    this.mounted = false;
  };

  // Deliberately does not reset entriesLoaded: refreshing after a delete should
  // not flash the skeleton rows.
  requestEntries = async () => {
    await apiRequest("api/waitingList/branch", "GET", {
      targetUserId: this.context.selectedUserId,
      clubId: this.state.clubId,
      branchId: this.state.branchId,
    })
      .then(this.handleEntries)
      .catch(this.handleLoadError);
  };

  handleEntries = ({ data }: any) => {
    if (!this.mounted) return;
    this.setState({ entriesLoaded: true });
    if (!data.success) {
      this.setState({ errorMsg: data.errorMsgDe });
      return;
    }

    const entryArr = data.data?.entries || [];
    const entryTableArr = entryArr.map((entry: any) => {
      const userName = `${entry.user?.fName || ""} ${
        entry.user?.lName || ""
      }`.trim();
      return {
        cells: [
          {
            value: entry.courseTitle,
            sort: entry.courseTitle,
            search: entry.courseTitle,
          },
          {
            value: userName,
            sort: entry.user?.lName,
            search: `${userName} ${entry.user?.email || ""}`,
          },
          {
            value: entry.user?.email || "-",
            sort: entry.user?.email,
            search: entry.user?.email,
          },
          {
            value: entry.user?.phoneNumber || "-",
            sort: entry.user?.phoneNumber,
          },
          {
            // The displayed text is relative ("Heute um ... Uhr"), so sort on
            // the raw seconds instead of the string.
            value: dateTosimpleDateStr(
              firebaseDateToDateObj(entry.created_at)
            ),
            sort: entry.created_at?._seconds,
          },
          {
            value: entry.courseDeleted
              ? "Angebot gelöscht"
              : entry.hasBooking
              ? "hat inzwischen gebucht"
              : entry.freePlaces
              ? `${entry.freePlaces} Plätze frei`
              : "wartet",
            sort: entry.hasBooking ? 1 : 0,
          },
          {
            value: (
              <WaitingListRemoveBtn
                onClick={() => {
                  this.askRemove(entry.id);
                }}
              />
            ),
          },
        ],
        // The row opens the customer profile, where the contact data lives.
        // Only branch members have one: a family sub-account can join a
        // guest-open course without being a member, and that profile page
        // renders blank for them. E-Mail and Telefon are in the table anyway.
        onClick:
          entry.userId && entry.isBranchMember
            ? `/club/${this.state.clubId}/branch-settings/${this.state.branchId}/users/${entry.userId}`
            : null,
        // Somebody who already has a place needs no contacting.
        opacity: entry.hasBooking ? 0.5 : undefined,
      };
    });

    this.setState({
      entryTableArr,
      entryCount: data.data?.totalCount || 0,
      errorMsg: null,
    });
  };

  handleLoadError = (err: any) => {
    console.error(err);
    if (!this.mounted) return;
    // apiRequest discards the body of a non-2xx response, so there is no
    // errorMsgDe available here.
    this.setState({
      entriesLoaded: true,
      errorMsg: "Die Warteliste konnte nicht geladen werden.",
    });
  };

  askRemove = (entryId: string) => {
    this.setState({ showConfirmationModal: true, selectedEntryId: entryId });
  };

  hideConfirmationModal = () => {
    this.setState({ showConfirmationModal: false });
  };

  removeConfirmed = async () => {
    const entryId = this.state.selectedEntryId;
    this.setState({ showConfirmationModal: false, errorMsg: null });
    // tries = 1: a retry would only produce a confusing "existiert nicht".
    await apiRequest(
      "api/waitingList/entry",
      "DELETE",
      {
        targetUserId: this.context.selectedUserId,
        clubId: this.state.clubId,
        branchId: this.state.branchId,
        entryId: entryId,
      },
      1
    )
      .then(this.handleRemoved)
      .catch(this.handleRemoveError);
  };

  handleRemoved = ({ data }: any) => {
    if (!this.mounted) return;
    if (data.success) {
      this.context.createInfo(
        "Der Eintrag wurde von der Warteliste entfernt.",
        "success",
        4
      );
      this.requestEntries();
    } else {
      this.setState({ errorMsg: data.errorMsgDe });
    }
  };

  handleRemoveError = (err: any) => {
    console.error(err);
    if (!this.mounted) return;
    this.setState({
      errorMsg: "Der Eintrag konnte nicht entfernt werden.",
    });
  };

  handleSearchChange = (val: string) => {
    this.setState({ searchVal: val });
  };

  handleRedirect = (redirect: string | null) => {
    // Table calls this even for rows with onClick: null.
    if (!redirect) return;
    // The back link goes into `search`: inside `pathname` the router does not
    // split it off, the profile route then does not match and the page stays
    // empty.
    this.props.history.push({
      pathname: redirect,
      search: `back=/club/${this.state.clubId}/branch/${this.state.branchId}/waiting-list`,
    });
  };

  render() {
    return (
      <>
        <ConfirmationModal
          show={this.state.showConfirmationModal}
          handleClose={this.hideConfirmationModal}
          title="Bist du sicher?"
          msg="Willst du diesen Eintrag wirklich von der Warteliste entfernen? Dieser Vorgang kann nicht rückgängig gemacht werden."
          handleConfirm={this.removeConfirmed}
        />
        <div className="subscreen-main-container">
          <div className="subscreen-box-container">
            <Title title="Warteliste" />
            <InfoContainer>
              Kunden tragen sich hier ein, wenn ein Angebot ausgebucht ist.
              Kontaktiere sie, sobald ein Platz frei wird, und entferne sie
              danach von der Liste.
            </InfoContainer>
            <SearchInput
              value={this.state.searchVal}
              onChange={this.handleSearchChange}
              placeholder="Angebot oder Kunde suchen"
            />
            <ErrorMessage message={this.state.errorMsg} />
            <Table
              header={[
                { title: "Kurs" },
                { title: "Kunde" },
                { title: "E-Mail", displayWidth: 1400 },
                { title: "Telefon", displayWidth: 1200 },
                { title: "Eingetragen am", displayWidth: 700 },
                { title: "Status", displayWidth: 950 },
                { title: "", disableSort: true },
              ]}
              data={this.state.entryTableArr}
              loaded={this.state.entriesLoaded}
              skeletonRows={5}
              onClick={this.handleRedirect}
              searchValue={this.state.searchVal}
              // Oldest first, so staff works the list first come first served.
              defaultSortDirection="asc"
              defaultSortIndex={4}
              counter
            />
            {this.state.entriesLoaded && this.state.entryCount === 0 && (
              <div className="full-center">
                <span className="not-exist-text">
                  Zurzeit steht niemand auf der Warteliste.
                </span>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }
}
